import type { API } from 'chessboard/api/config/types';
import { Game } from 'chessboard/model/game/types';
import JCE from '@chesscom/js-chess-engine';

/**
 * Performs type narrowing using type predicates
 */
function isJCEMoveModel(move: JCE.AnyMoveType): move is JCE.Move.Model {
  return (move as JCE.Move.Model).color !== undefined;
}

export interface ExtendedJCEPositionInfo extends JCE.Position.Info {
  timeoutVsInsufficientMaterial?: boolean;
  lastMoveColor?: Game.ColorsAsNumbers;
  drawByAgreement?: boolean;
}

export type MetaGameTerminationValues = {
  drawByAgreement: string;
  timeoutVsInsufficientMaterial: string;
};

/**
 * Create the getPositionInfo proxy method
 * for JCE.getPositionInfo()
 */
export function createGetPositionInfo(context: API.Context) {
  /**
   * Returns game state information about the position:
   *
   * @example
   *
   * {
   *   gameOver: true,
   *   check: true,
   *   checkmate: true,
   *   draw: false,
   *   stalemate: false,
   *   threefold: false,
   *   insufficient: false,
   *   fiftyMoveRule: false,
   *   isValid: true
   * }
   */
  return function getPositionInfo(
    moves: JCE.AnyMoveType[] | string = [],
    fromFen?: string,
    metaGameTerminationValues?: MetaGameTerminationValues,
  ): false | ExtendedJCEPositionInfo {
    const { api } = context;

    // This method has different behavior for detecting threefold repetition depending on `fromFen`:
    // 1. `fromFen: undefined`
    //   - correctly identifies threefold, uses game object which has all the history.
    // 2. `fromFen: '...', moves: []`
    //   - will not identify threefold, fen has no history.
    // 3. `fromFen: '...', moves: [...]
    //   - will identify threefold only if the repetitions are in the supplied moves.
    // 4. `fromFen: <initial fen>, moves: <all game moves>`
    //   - correctly identifies threefold (see getFinalPositionInfo)

    const initialPosition = fromFen ?? {
      game: context.game,
      analysis: true,
    };

    // For some methods, JCE uses the long form variant type,
    // e.g. "King of the Hill" instead of "kingofthehill"
    const variant = <JCE.GameTypes>Game.JCEVariantTypes[api.getVariant()];

    const info = JCE.getPositionInfo(initialPosition, moves, { variant });

    if (typeof info === 'object') {
      const termination = context.game.header().Termination;

      const positionInfo: ExtendedJCEPositionInfo = {
        ...info,

        ...getMetaGameTerminationReasons(termination, metaGameTerminationValues),

        // JCE does not take into account claimable draws, so we must check the api for a claimed draw
        // before falling back to the info returned from JCE
        gameOver: api.isGameOver() || info.gameOver,
        draw: api.getResult() === Game.Results.Draw || info.draw,
      };

      if (typeof moves !== 'string') {
        const lastMove = moves[moves.length - 1];
        if (lastMove !== undefined && isJCEMoveModel(lastMove)) {
          positionInfo.lastMoveColor = lastMove.color;
        }
      }

      return positionInfo;
    }

    return false;
  };
}

/**
 * Returns the meta game termination reasons. Empty object if metaGameTerminationValues is undefined.
 */
function getMetaGameTerminationReasons(
  terminationStr: string | undefined,
  metaGameTerminationValues: MetaGameTerminationValues | undefined,
):
  | {
      timeoutVsInsufficientMaterial: boolean;
      drawByAgreement: boolean;
    }
  | {} {
  if (!metaGameTerminationValues) {
    return {};
  }

  return {
    drawByAgreement: terminationStr === metaGameTerminationValues.drawByAgreement,
    timeoutVsInsufficientMaterial:
      terminationStr === metaGameTerminationValues.timeoutVsInsufficientMaterial,
  };
}
